import { useEffect, useMemo, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { unwrapResult } from "@reduxjs/toolkit";
import { Localized } from "@fluent/react";
import { useAppDispatch } from "../../app/hooks";
import { CounterpartyListItem, loadCounterparties } from "./counterpartyApi";

type Props = {
    value: string;
    onChange: (publicId: string) => void;
    label?: React.ReactNode;
    disabled?: boolean;
};

export const CounterpartySelect = ({ value, onChange, label, disabled = false }: Props) => {
    const dispatch = useAppDispatch();
    const [options, setOptions] = useState<CounterpartyListItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadCounterparties())
            .then(unwrapResult)
            .then((data) => setOptions(data ?? []))
            .catch((err) => {
                console.error(err);
                setOptions([]);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch]);

    const selected = useMemo(
        () => options.find((x) => x.publicId === value) ?? null,
        [options, value]
    );

    return (
        <Autocomplete
            fullWidth
            options={options}
            value={selected}
            loading={isLoading}
            disabled={disabled}
            clearOnEscape
            isOptionEqualToValue={(a, b) => a.publicId === b.publicId}
            getOptionLabel={(option) => option.displayName ?? ""}
            onChange={(_, newValue) => onChange(newValue?.publicId ?? "")}
            renderOption={(props, option) => (
                <li {...props} key={option.publicId}>
                    {option.displayName}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={
                        label ?? (
                            <Localized id="counterparties-select-label">
                                <span>Kontrahent</span>
                            </Localized>
                        )
                    }
                    sx={{ mt: 2 }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
